//REV-21367 — types of the closing document hub (Pure View).
//
//REV-21368 promoted the SHARED vocabulary of the document flow into
//`lib/types/closing-documents.types.ts`, because the single-document page speaks the same
//language and a sibling component must not import out of this folder. The re-export below keeps
//every existing import path working — nothing that imports from here has to change.
//
//What stays here is what only the HUB has: the table's row shape, its three filter pills and its
//sortable columns. `AccountAssignEvent` moved too — both pickers ask account first and validity
//second, so the payload is shared, not hub-specific.

export {
    AccountAssignEvent,
    AccountOption,
    AssignmentOrigin,
    AssignmentState,
    AssignmentTarget,
    DocumentAccountRef,
    DocumentFiling,
    DocumentReadingState,
    DocumentSource,
    DocumentUpstreamState,
    DocumentValidity,
    MarkNotRelevantEvent,
    SharepointSkipReason,
    SyncRunStatus,
    SyncStartRefusal,
} from '../../types/closing-documents.types';

import { SortState } from '../sort-header/sort-header.types';
import {
    AssignmentOrigin,
    AssignmentState,
    BalanceCheck,
    DocumentAccountRef,
    DocumentFiling,
    DocumentProcessingStep,
    DocumentReadingState,
    DocumentSource,
    DocumentUpstreamState,
    DocumentValidity,
    ProposalCertainty,
    ProposalSource,
    SharepointSkipReason,
    SyncRunStatus,
} from '../../types/closing-documents.types';

export interface DocumentRow {
    id: string;
    filename: string;
    /**
     * The booking owner when `assignment === 'assigned'`, the PROPOSED account when
     * `'proposed'`. `null` for `'open'` and for every `projectFile` row.
     */
    account: DocumentAccountRef | null;
    filing: DocumentFiling;
    assignment: AssignmentState;
    /**
     * Only read when `assignment === 'assigned'`. Absent/`null` is LEGACY DATA (#57) — a document
     * assigned before this field existed — and is deliberately NOT `'manual'`: nobody knows who
     * filed it, and claiming a human confirmed it would be a fact the row does not have.
     */
    assignedBy?: AssignmentOrigin | null;
    /**
     * 0–100, or `null` → renders `—`. Absent is the NORMAL state until REV-21358 lands
     * (ADR-0009 §8) and must never read as an error.
     */
    confidence: number | null;
    /** ISO-8601. The view formats it; it never parses or re-times it. */
    addedAt: string;
    source: DocumentSource;
    /** Orthogonal to `filing`: marking preserves the filing so un-marking restores the old pill. */
    notRelevant: boolean;
    validity: DocumentValidity;
    /** Pre-composed Dauerakte tooltip ("… langfristig gültig bis 31.3.2027"). */
    validityHint?: string;
    /**
     * REV-21964 · C1–C4 — where the upstream file stands. Absent means `present`, which is also what
     * every manual upload is: SharePoint has no opinion about a file it never held.
     *
     * ⚠️ This NEVER hides the row and never changes the account column. A document whose file was
     * deleted in SharePoint stays in the closing with its account and its reconciliation intact
     * (C1); the row's job is to SAY so. ADR-0009 §4 has no delete path, and `notRelevant` remains
     * the only retreat — reversible, and the trustee's.
     */
    upstreamState?: DocumentUpstreamState;
    /**
     * `true` when the state is the LOUD kind (C2) — the document backs an account.
     *
     * The DECISION is the container's and arrives as a flag; the WORDING is the view's, chosen from
     * a total map over the closed union. A pre-composed sentence was the earlier shape and it bought
     * nothing: nobody produced one, the view needed its own fallback anyway, and prose in a selector
     * is translation in the store.
     *
     * What the view must never do is re-derive "is this assigned?" from the columns it happens to
     * render — that is how two places come to disagree about one fact, which is why the flag and not
     * the derivation crosses the boundary.
     */
    upstreamNoticeIsUrgent?: boolean;
    /** ID2 — pre-composed "Weitere Namen in SharePoint: …", or absent when there are none. */
    aliasHint?: string;
    /**
     * REV-22010 — has the AI read the file? Absent = read, and then no marker is drawn. A LABEL beside
     * the name, like the upstream marker: it never hides the row or changes another column.
     */
    readingState?: DocumentReadingState;
    /**
     * REV-22302 — the recognised Belegart (kernel `DocumentKind` identifier), shown as a label in the
     * Belegtyp column. Absent = unknown → `—`. The view chooses the label from a total map; the
     * identifier is never rendered.
     */
    kind?: string | null;
    /**
     * REV-22302 — the classifier's raw refusal code while it is the last word about the document.
     * Drives the `noProposal` state (`presenters/document-ai-state.ts`); the view picks the sentence
     * from a total map, with a generic one for a code it does not know.
     */
    proposalRefusalCode?: string | null;
    /** REV-22302 — a human rejected the live proposal; `noProposal` with its own reason. */
    proposalRejected?: boolean;
    /** REV-22302 — the second candidate account (the other strand's pick), shown as its own chip. */
    alternativeAccountId?: string;
    proposalSource?: ProposalSource;
    proposalCertainty?: ProposalCertainty;
    balanceCheck?: BalanceCheck;
    processingStep?: DocumentProcessingStep;
    /**
     * The document's balance is dated other than the closing's balance-sheet date (ISO dates). A
     * marker beside the name: the document still counts as filed where it is, it just proves nothing
     * for this closing.
     */
    dateWarning?: { balanceDate: string; balanceSheetDate: string } | null;
}

/**
 * The three pills of ADR-0009 §9a. They are a TRUE PARTITION — every document sits in exactly
 * one, and the three counts sum to the total:
 *   inbox       = filing === 'unfiled'                        && !notRelevant
 *   assigned    = filing ∈ { 'account', 'projectFile' }        && !notRelevant
 *   notRelevant = notRelevant                                 (regardless of filing)
 */
export type DocumentFilterKey = 'inbox' | 'assigned' | 'notRelevant';

/** REV-22302 — a click on a candidate chip: accept the proposal with exactly that account. */
export interface ProposalAcceptEvent {
    documentId: string;
    accountId: string;
}

export interface DocumentFilterCounts {
    inbox: number;
    assigned: number;
    notRelevant: number;
}

export type DocumentSortKey = 'filename' | 'account' | 'confidence' | 'addedAt' | 'source' | 'assignment';

/** A column sort of the hub — the shared shape every sortable ABCP table uses. */
export type DocumentSort = SortState<DocumentSortKey>;

// ─── REV-21964 · U1 — the SYNC REPORT, as the hub renders it ────────────────────────────────────
//
// A run's report lives here and not in the shared vocabulary because only the HUB shows it: the
// single-document page answers for one document and has no opinion about a folder walk.
//
// ⚠️ It carries REASONS, not sentences. The container maps the wire run onto this shape and stops
// there; the view owns the i18n key for each reason, because choosing a key from a closed union is
// presentation and doing it in a selector would put translation in the store. What the container
// must NOT do is pre-compose the prose — that is how the same skip ends up worded two ways.

/** One file that did not become a document, and why (ADR-0009 §12: never silently dropped). */
export interface SyncSkipLine {
    filename: string;
    reason: SharepointSkipReason;
}

/**
 * One bound folder that could not be read at all (R1).
 *
 * A FAILED FOLDER is not a skipped file: a skip means "we looked and decided", a failed folder means
 * "we never got to look", and the whole point of reporting them apart is that the second one hides
 * an unknown number of documents.
 */
export interface SyncFolderFailure {
    folderPath: string;
    /** A message, never a raw error — it is rendered to a trustee. Absent renders the folder alone. */
    message?: string | null;
}

/**
 * The last run's report.
 *
 * ⚠️ Read back from the SERVER, not remembered by the tab. REV-21376 returned the same information
 * in the fetch response and kept none of it, so "why is my document not there?" had an answer for
 * exactly as long as the tab stayed open — and the one moment a trustee asks that question is after
 * they have navigated away and come back.
 */
export interface SyncReportView {
    status: SyncRunStatus;
    /** ISO-8601. The view formats it; it never parses or re-times it. */
    startedAt: string;
    finishedAt?: string | null;
    discovered: number;
    created: number;
    skipped: number;
    /** Folders that could not be read (R1). Counted even when the list below is empty. */
    failedFolders: number;
    skips: SyncSkipLine[];
    /**
     * `true` when MORE files were skipped than the run stores.
     *
     * ⚠️ The view must SAY this. The count stays exact while the list stops, and a list that merely
     * ended would tell the trustee that everything not named succeeded — the silent truncation
     * ADR-0009 §12 forbids.
     */
    skipsTruncated: boolean;
    failures: SyncFolderFailure[];
}

/**
 * Which skips are a real problem. A file that is already in the closing, or whose bytes are, is
 * fine and must not turn the bar orange. Total, so a new reason cannot compile without a decision.
 */
const SKIP_IS_PROBLEM: Record<SharepointSkipReason, boolean> = {
    unsupportedType: true,
    tooLarge: true,
    downloadFailed: true,
    rejectedByIngest: true,
    listingCapped: true,
    alreadyImported: false,
    duplicateContent: false,
};

/** Files the run did not take over because of a real problem. Counts what the stored list holds. */
export function problemSkipCount(skips: SyncSkipLine[]): number {
    return skips.filter((skip) => SKIP_IS_PROBLEM[skip.reason]).length;
}
